import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Search } from 'lucide-react';
import { aiInterviewsAPI } from '../../../lib/aiInterviewApi';
import { DECISION_LABEL, Empty, fmtDate, Loading, RecPill, StatusPill, TierPill } from './common';

const FILTERS = [
  ['all', 'All', () => true],
  ['invited', 'Invited', (r) => r.status === 'invited'],
  ['opened', 'Opened', (r) => r.status === 'opened'],
  ['consented', 'Consented', (r) => r.status === 'consented'],
  ['in_progress', 'In progress', (r) => ['in_progress', 'starting', 'interrupted'].includes(r.status)],
  ['interrupted', 'Interrupted', (r) => r.status === 'interrupted'],
  ['completed', 'Completed', (r) => ['completed', 'scoring', 'score_error'].includes(r.status)],
  ['report_ready', 'Report ready', (r) => r.status === 'report_ready'],
  ['declined', 'Declined', (r) => r.status === 'declined'],
  ['expired', 'Expired', (r) => ['expired', 'revoked'].includes(r.status)],
  ['needs_review', 'Needs review', (r) => r.review_status === 'pending'],
];

export const InterviewTable = ({ rows, testId = 'interviews-table' }) => {
  const navigate = useNavigate();
  return (
    <div className="iv-scroll-x">
      <table className="iv-table" data-testid={testId}>
        <thead>
          <tr><th>Candidate</th><th>Role</th><th>Status</th><th>AI score</th><th>Integrity</th><th>Decision</th><th>Invited</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} onClick={() => navigate(`/ai-interviews/${r.id}`)} data-testid={`interview-row-${r.id}`}>
              <td><div style={{ fontWeight: 700, color: '#002A38' }}>{r.candidate_name}</div><div className="iv-small">{r.candidate_email || 'No email'}</div></td>
              <td>{r.job_title}</td>
              <td><StatusPill status={r.status} /></td>
              <td>{typeof r.score_100 === 'number' ? <div className="iv-row" style={{ gap: '0.4rem' }}><b>{r.score_100}</b><RecPill rec={r.recommendation} /></div> : <span className="iv-small">—</span>}</td>
              <td>{['invited', 'opened', 'declined', 'expired', 'revoked'].includes(r.status) ? <span className="iv-small">—</span> : <TierPill tier={r.integrity_tier} score={r.risk_score} />}</td>
              <td>{r.decision ? DECISION_LABEL[r.decision] : <span className="iv-small">Pending</span>}</td>
              <td className="iv-small">{fmtDate(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function InterviewsBoard({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    aiInterviewsAPI.list().then(({ data }) => setRows(data)).catch(() => { toast.error('Could not load AI interviews'); setRows([]); });
  }, [refreshKey]);

  const counts = useMemo(() => Object.fromEntries(FILTERS.map(([k, , fn]) => [k, (rows || []).filter(fn).length])), [rows]);
  const shown = useMemo(() => {
    const fn = FILTERS.find(([k]) => k === filter)[2];
    const needle = q.trim().toLowerCase();
    return (rows || []).filter(fn).filter((r) => !needle || `${r.candidate_name} ${r.candidate_email} ${r.job_title}`.toLowerCase().includes(needle));
  }, [rows, filter, q]);

  if (!rows) return <Loading />;
  if (!rows.length) {
    return <Empty testId="interviews-board-empty" title="No AI interviews yet" body="Create an interview plan for a job, then invite shortlisted candidates. Each candidate gets a private link." />;
  }
  return (
    <div data-testid="interviews-board">
      <div className="iv-row" style={{ justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div className="iv-chips" style={{ marginBottom: 0 }}>
          {FILTERS.map(([k, label]) => (
            <button type="button" key={k} className={`iv-chip ${filter === k ? 'is-on' : ''}`} onClick={() => setFilter(k)} data-testid={`board-filter-${k}`}>
              {label} <span style={{ opacity: 0.6 }}>{counts[k]}</span>
            </button>
          ))}
        </div>
        <div className="iv-row" style={{ position: 'relative', minWidth: 220 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, color: '#94A3B8' }} />
          <input className="iv-input" style={{ paddingLeft: 32 }} placeholder="Search candidate or role" value={q} onChange={(e) => setQ(e.target.value)} data-testid="board-search-input" />
        </div>
      </div>
      {shown.length ? <InterviewTable rows={shown} /> : <Empty testId="interviews-board-filter-empty" title="No interviews match this filter" />}
    </div>
  );
}
